import { Edit, useForm } from "@refinedev/antd";
import { HttpError } from "@refinedev/core";
import { Button, DatePicker, Form, Input, InputNumber, Select, Switch } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { IconDisplay } from "../../components/IconDisplay";
import { ImageUpload } from "../../components/ImageUpload";

export function AdCreativeEdit() {
  const [showMediaUpload, setShowMediaUpload] = useState(false);
  const [showPosterUpload, setShowPosterUpload] = useState(false);

  const { formProps, saveButtonProps, query, formLoading } = useForm<any, HttpError, any>({
    resource: "ad-creative",
    successNotification: (data) => ({ message: data?.data.message, type: "success" }),
    action: "edit",
  });

  const ad = query?.data?.data?.data;

  const handleFinish = (values: any) => {
    const v = { ...values };
    if (!v.ctaType) {
      v.ctaType = null;
      v.ctaTarget = null;
    }
    if (v.mediaType === "image") v.posterID = null;
    return formProps.onFinish?.(v);
  };

  return (
    <Edit
      saveButtonProps={saveButtonProps}
      isLoading={formLoading}
      resource="ad-creative"
      mutationMode="optimistic"
      title="Edit ad creative"
    >
      <Form {...formProps} onFinish={handleFinish} layout="vertical">
        <Form.Item label="Media type" name={["mediaType"]} initialValue={ad?.mediaType} rules={[{ required: true }]}>
          <Select
            options={[
              { label: "Image", value: "image" },
              { label: "Video", value: "video" },
            ]}
          />
        </Form.Item>

        <Form.Item label="Media" name="mediaID" initialValue={ad?.mediaFileUploadId ?? undefined}>
          {!showMediaUpload ? (
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <IconDisplay iconURL={ad?.posterURL || ad?.mediaURL} width={64} height={64} borderRadius="8px" />
              <Button onClick={() => setShowMediaUpload(true)}>Change media</Button>
            </div>
          ) : (
            <ImageUpload uploadType="AD_CREATIVE" accept="image/*,video/*" />
          )}
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(p, c) => p.mediaType !== c.mediaType}>
          {({ getFieldValue }) =>
            getFieldValue("mediaType") === "video" ? (
              <Form.Item label="Poster (still frame)" name="posterID" initialValue={ad?.posterFileUploadId ?? undefined}>
                {!showPosterUpload ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                    <IconDisplay iconURL={ad?.posterURL} width={64} height={64} borderRadius="8px" />
                    <Button onClick={() => setShowPosterUpload(true)}>Change poster</Button>
                  </div>
                ) : (
                  <ImageUpload uploadType="AD_CREATIVE" accept="image/*" />
                )}
              </Form.Item>
            ) : null
          }
        </Form.Item>

        <Form.Item label="House inventory (GymLive)" name={["isHouse"]} valuePropName="checked" initialValue={ad?.isHouse}>
          <Switch />
        </Form.Item>

        <Form.Item label="Brand" name={["brand"]} initialValue={ad?.brand} rules={[{ required: true }]}>
          <Input />
        </Form.Item>

        <Form.Item label="Mark" name={["mark"]} initialValue={ad?.mark} rules={[{ required: true }]}>
          <Input maxLength={4} />
        </Form.Item>

        <Form.Item label="Title" name={["title"]} initialValue={ad?.title} rules={[{ required: true }]}>
          <Input />
        </Form.Item>

        <Form.Item label="Subtitle" name={["sub"]} initialValue={ad?.sub ?? undefined}>
          <Input />
        </Form.Item>

        <Form.Item label="CTA label" name={["cta"]} initialValue={ad?.cta} rules={[{ required: true }]}>
          <Input />
        </Form.Item>

        <Form.Item label="CTA destination" name={["ctaType"]} initialValue={ad?.ctaType ?? ""}>
          <Select
            options={[
              { label: "None (not tappable)", value: "" },
              { label: "In-app screen", value: "in_app" },
              { label: "External URL", value: "external" },
            ]}
          />
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(p, c) => p.ctaType !== c.ctaType}>
          {({ getFieldValue }) => {
            const t = getFieldValue("ctaType");
            if (!t) return null;
            return (
              <Form.Item
                label={t === "external" ? "URL" : "Screen key"}
                name="ctaTarget"
                initialValue={ad?.ctaTarget ?? undefined}
                rules={[{ required: true }]}
              >
                <Input placeholder={t === "external" ? "https://example.com" : "program"} />
              </Form.Item>
            );
          }}
        </Form.Item>

        <Form.Item label="Priority" name={["priority"]} initialValue={ad?.priority ?? 0}>
          <InputNumber min={0} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item label="Starts at" name={["startsAt"]} initialValue={ad?.startsAt ? dayjs(ad.startsAt) : undefined}>
          <DatePicker showTime style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item label="Ends at" name={["endsAt"]} initialValue={ad?.endsAt ? dayjs(ad.endsAt) : undefined}>
          <DatePicker showTime style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item label="Active" name={["isActive"]} valuePropName="checked" initialValue={ad?.isActive}>
          <Switch />
        </Form.Item>
      </Form>
    </Edit>
  );
}
